import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// --- external imports ------

import { validateEmail, errorText } from '~/authentication/lib/credentialValidation';
import { EMAIL_MAX_LENGTH } from '~/authentication/lib/constants-auth';
import useAuthRequest from '~/authentication/lib/useAuthRequest';
import { useUserContext } from '~/authentication/UserProvider';
import { recordLinkSent, wasLinkSent } from '~/authentication/lib/sentLinkNotice';
import { LoadingCursor } from '~/containers/GameAsset/cursors';
// --- internal imports ------

import resendRegistrationEmail from './apis/resendRegistrationEmail';
import AuthBackdrop from '../AuthBackdrop';
import {
	ModalBackground,
	TitleText,
	InputBackground,
} from '~/authentication/AuthAssets';
import {
	BodyText,
	EmailLabel,
	ErrorText,
	EmailInput,
	RequestButton,
	RequestButtonText,
	BackToLoginLink,
	TITLE_PROJECTION,
	BODY_TEXT_PROJECTION,
	EMAIL_LABEL_PROJECTION,
	INPUT_BG_PROJECTION,
	EMAIL_INPUT_PROJECTION,
} from './InvalidLinkAssets';
// --- local imports ------

// Said in both directions: by the send that has just happened, and by the record
// of one that happened before a reload. One string, so the two cannot drift.
const LINK_SENT_MESSAGE = 'We have sent an email to the specified account. Please \
	check your email. If you fail to find the email, please check your spam.';

/**
 * The screen that hands out a new link, in two situations.
 *
 * By default it is the dead-link case `EmailLoginRedirector` renders: nobody is
 * signed in, so the player types the address they want the new link sent to.
 *
 * `lockedEmail` is the other one. Registration reached a session that could no
 * longer set a password — the sign-in went stale, or there was never a Firebase
 * half to it — and there the player is already known. Asking them to type an
 * address we are holding invites them to type a different one and wonder why no
 * mail arrives, so the field shows theirs and does not take input. The button is
 * then the only thing on the screen to press, which is the point: nothing moves
 * until they choose to move it.
 */
function InvalidLink(props) {
	const { title = 'INVALID LINK', bodyText, lockedEmail } = props;

	const [emailError, setEmailError] = useState(null);
	// Seeded from the record rather than from `null`: the send outlives the mount
	// that made it, so the confirmation has to as well. Without this a reload
	// while waiting for the email showed the request form again, which reads as
	// "nothing happened" and invites a second request for a link already sent.
	const [responseMessage, setResponseMessage] = useState(
		() => (wasLinkSent() ? LINK_SENT_MESSAGE : null)
	);
	const emailInputRef = useRef(null);
	const { isProcessing, runRequest } = useAuthRequest();
	const { logOutPlayer } = useUserContext();
	const navigate = useNavigate();

	const handleOnSubmit = useCallback(async () => {
		// A locked address is ours rather than theirs, and it is not validated: it
		// came from the session that just failed, and the player has no way to
		// correct it if we decide we dislike it.
		const email = lockedEmail ?? emailInputRef.current?.value ?? '';

		if (!lockedEmail) {
			const { isValid: isValidEmail, error: emailErr } = validateEmail(email);
			setEmailError(emailErr);
			if (!isValidEmail) {
				return;
			}
		}

		try {
			const response = await runRequest(() => resendRegistrationEmail(email));

			const hasAlreadySetUpUser = response?.data?.hasAlreadySetUpUser;
			if (hasAlreadySetUpUser === true) {
				setResponseMessage('No email has been sent to you, as you\'re already \
					registered. If you can not log in, please attempt to use the Forgot \
					Password form.');
			}
			else if (hasAlreadySetUpUser === false) {
				// The one outcome worth writing down. "Already registered" is a standing
				// fact rather than something in flight, and a failure has to leave the
				// button pressable for the player whose request did not land.
				recordLinkSent();
				setResponseMessage(LINK_SENT_MESSAGE);
			}
			else {
				setResponseMessage('An unexpected error has occured.');
			}
		}
		catch (e) {
			console.error(e);
			setResponseMessage('Failed to send the link. \
				Please try again later.');
		}
	}, [runRequest, lockedEmail]);

	// Back To Login has to end the session, not just change the URL.
	//
	// When `RegisterPage` renders this screen, the session that could not set a
	// password is still here on purpose — clearing it would take the explanation
	// off the screen with it. But `AuthRoute` redirects to /game on the strength
	// of a Firebase user and a token, and /game hands an unfinished registration
	// straight back to `RegisterPage`, which remounts with a fresh
	// `hasUnusableSignIn` and shows the registration form again. So a plain link
	// to /login walked the player back into the form they had just been told to
	// give up on, one press after we told them to go and read their email.
	//
	// Pressing it is the moment the player gives the session up, which is the
	// moment it is safe to drop: the new link is what signs them in next, and the
	// screen they are leaving has already said so.
	//
	// Not awaited. `logOutPlayer` drops the token and clears the context before
	// its first await, so the session is over by the time this returns; what it
	// waits for is `firebase.signOut()`, which reaches IndexedDB and can block
	// behind another tab. Waiting for that would leave the player pressing a link
	// that does nothing. It handles its own failure and cannot reject.
	const handleBackToLogin = useCallback((event) => {
		event.preventDefault();

		// Absent only where this screen is rendered outside the provider. There is
		// no session to end in that case, and the navigation below is the whole job.
		logOutPlayer?.();
		navigate('/login', { replace: true });
	}, [logOutPlayer, navigate]);

	const handleOnEmailBlur = useCallback((e) => {
		const { error: emailErr } = validateEmail(e.target.value);
		setEmailError(emailErr);
	}, []);

	function renderContent() {
		if (responseMessage !== null) {
			return (
				<>
					<BodyText
						x={644}
						y={468}
						projection={BODY_TEXT_PROJECTION}
						shouldFadeIn={false}
					>
						{responseMessage}
					</BodyText>
					<BackToLoginLink
						to="/login"
						onClick={handleBackToLogin}
						x={644}
						y={770}
						fontSize={24}
						shouldFadeIn={false}
					>
						Back To Login
					</BackToLoginLink>
				</>
			);
		}

		return (
			<>
				<BodyText
					x={644}
					y={468}
					fontSize={32}
					projection={BODY_TEXT_PROJECTION}
					shouldFadeIn={false}
				>
					{bodyText ?? (
						<>
							Sorry, this link has expired.<br />
							Please enter a registered E-mail address and we will send you a new link to login.
						</>
					)}
				</BodyText>

				<EmailLabel
					x={644}
					y={668}
					projection={EMAIL_LABEL_PROJECTION}
					fontSize={32}
					shouldFadeIn={false}
				>
					E-Mail
				</EmailLabel>

				<InputBackground
					x={644}
					y={724}
					projection={INPUT_BG_PROJECTION}
					shouldFadeIn={false}
				/>

				<EmailInput
					ref={emailInputRef}
					x={644}
					y={724}
					projection={EMAIL_INPUT_PROJECTION}
					fontSize={24}
					id="email"
					name="email"
					type="email"
					maxLength={EMAIL_MAX_LENGTH}
					placeholder="E-Mail"
					// `readOnly`, not `disabled`: the address is what the screen is
					// telling them, so it has to stay legible rather than greyed out.
					readOnly={!!lockedEmail}
					defaultValue={lockedEmail}
					onBlur={lockedEmail ? undefined : handleOnEmailBlur}
					disabled={isProcessing}
					hasError={!!emailError}
					shouldFadeIn={false}
				/>

				{emailError && (
					<ErrorText
						x={644}
						y={812}
						projection={BODY_TEXT_PROJECTION}
						fontSize={20}
						shouldFadeIn={false}
					>
						{errorText(emailError)}
					</ErrorText>
				)}
			</>
		);
	}

	return (
		<AuthBackdrop>
			{isProcessing && <LoadingCursor />}

			<ModalBackground
				x={528}
				y={148}
				shouldFadeIn={false}
			/>

			<TitleText
				x={734.5}
				y={212}
				projection={TITLE_PROJECTION}
				fontSize={128}
				shouldFadeIn={false}
			>
				{title}
			</TitleText>

			{renderContent()}

			{responseMessage === null && (
				<>
					<RequestButton
						x={865}
						y={896}
						onClick={handleOnSubmit}
						disabled={isProcessing}
						shouldFadeIn={false}
					/>
					<RequestButtonText
						x={881}
						y={907}
						shouldFadeIn={false}
					>
						Request Link
					</RequestButtonText>
				</>
			)}
		</AuthBackdrop>
	);
}

export default InvalidLink;
